import React from 'react';
import {
  Grid, Paper, Typography, Box, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Divider, Alert,
} from '@mui/material';
import { useReconciliation } from '../../../hooks/useForm16';
import AnnexureAEditor from './AnnexureAEditor';
import AnnexureBEditor from './AnnexureBEditor';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Form16PartA({ form16, onChanged, readOnly = false }) {
  const partA = form16.part_a;
  const { data: recon } = useReconciliation(readOnly ? null : form16.id);

  if (!partA) {
    return <Alert severity="info">Part A snapshot is not generated yet. Click "Refresh from payroll" above.</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Employer (Deductor)</Typography>
            <Typography variant="body2"><b>{partA.employer_name}</b></Typography>
            <Typography variant="body2">{partA.employer_address}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" display="block">PAN: {partA.employer_pan}</Typography>
            <Typography variant="caption" display="block">TAN: {partA.employer_tan}</Typography>
            <Typography variant="caption" display="block">CIT(TDS): {partA.cit_tds_address || '—'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Employee</Typography>
            <Typography variant="body2"><b>{partA.employee_name}</b></Typography>
            <Typography variant="body2">{partA.employee_address}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" display="block">PAN: {partA.employee_pan || '—'}</Typography>
            <Typography variant="caption" display="block">Designation: {partA.employee_designation || '—'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Quarterly Summary</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Quarter</TableCell>
              <TableCell>Receipt No.</TableCell>
              <TableCell align="right">Salary Paid (₹)</TableCell>
              <TableCell align="right">TDS Deducted (₹)</TableCell>
              <TableCell align="right">TDS Deposited (₹)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4].map((q) => (
              <TableRow key={q}>
                <TableCell>Q{q}</TableCell>
                <TableCell>{partA[`q${q}_receipt_no`] || '—'}</TableCell>
                <TableCell align="right">{fmt(partA[`q${q}_salary_paid`])}</TableCell>
                <TableCell align="right">{fmt(partA[`q${q}_tds_deducted`])}</TableCell>
                <TableCell align="right">{fmt(partA[`q${q}_tds_deposited`])}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell colSpan={2}><b>Total</b></TableCell>
              <TableCell align="right"><b>{fmt(partA.total_salary_paid)}</b></TableCell>
              <TableCell align="right"><b>{fmt(partA.total_tds_deducted)}</b></TableCell>
              <TableCell align="right"><b>{fmt(partA.total_tds_deposited)}</b></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!readOnly && recon && !recon.error && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Reconciliation against payroll TDS aggregate (Annexure totals must match before publishing):
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {Object.entries(recon).map(([q, vals]) => {
              const delta = Number(vals.delta);
              const ok = Math.abs(delta) <= 1;
              return (
                <Alert key={q} severity={ok ? 'success' : 'warning'} sx={{ flex: '1 1 200px' }}>
                  <b>{q}</b>: payroll {vals.payroll}, annexure {vals.annexure}, delta {vals.delta}
                </Alert>
              );
            })}
          </Box>
        </Box>
      )}

      {!readOnly ? (
        <>
          <AnnexureAEditor partAId={partA.id} onChanged={onChanged} />
          <AnnexureBEditor partAId={partA.id} onChanged={onChanged} />
        </>
      ) : (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Annexure A — Book Entry deposits</Typography>
          <ReadOnlyAnnexure rows={partA.book_deposits || []} variant="A" />
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Annexure B — Challan deposits</Typography>
          <ReadOnlyAnnexure rows={partA.challans || []} variant="B" />
        </Box>
      )}
    </Box>
  );
}

function ReadOnlyAnnexure({ rows, variant }) {
  if (!rows.length) {
    return <Typography variant="body2" color="text.secondary">No entries.</Typography>;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Quarter</TableCell>
          <TableCell align="right">TDS Amount</TableCell>
          {variant === 'A' ? (
            <>
              <TableCell>Receipt No. (Form 24G)</TableCell>
              <TableCell>DDO Sequence No.</TableCell>
            </>
          ) : (
            <>
              <TableCell>BSR Code</TableCell>
              <TableCell>Challan Serial No.</TableCell>
            </>
          )}
          <TableCell>Deposit Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell>Q{r.quarter}</TableCell>
            <TableCell align="right">{r.tds_amount}</TableCell>
            {variant === 'A' ? (
              <>
                <TableCell>{r.receipt_no_24g || '—'}</TableCell>
                <TableCell>{r.ddo_sequence_no || '—'}</TableCell>
              </>
            ) : (
              <>
                <TableCell>{r.bsr_code || '—'}</TableCell>
                <TableCell>{r.challan_serial_no || '—'}</TableCell>
              </>
            )}
            <TableCell>{r.deposit_date || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
